import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useAdminReplyMessage } from '@/hooks/use-bug-report-detail';
import type { SupportMessage } from '@/types/bug-reports';

export function ConversationThread({
  reference,
  messages,
}: {
  reference: string;
  messages: SupportMessage[];
}): JSX.Element {
  const [draft, setDraft] = useState('');
  const reply = useAdminReplyMessage();
  const canSend = draft.trim().length > 0 && !reply.isPending;

  function handleSend(): void {
    if (!canSend) return;
    reply.mutate(
      { reference, body: draft.trim() },
      {
        onSuccess: () => setDraft(''),
        onError: () => toast.error("Couldn't send the reply. Try again."),
      }
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {messages.length === 0 ? (
        <p className="text-sm text-muted-foreground">No messages yet.</p>
      ) : (
        <div className="flex flex-col gap-2">
          {messages.map((message, i) => (
            <div
              key={`${message.createdAt}-${i}`}
              className={message.senderType === 'admin' ? 'self-end text-right' : 'self-start'}
            >
              <p className="text-xs text-muted-foreground">
                {message.senderType === 'admin' ? 'You' : `User ${message.senderId}`}
              </p>
              <p className="rounded bg-muted p-2 text-sm">{message.body}</p>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Reply to the reporter..."
          className="flex-1"
        />
        <Button onClick={handleSend} disabled={!canSend}>
          Send
        </Button>
      </div>
    </div>
  );
}
